import { useMemo, useState, useSyncExternalStore, type ChangeEvent } from 'react';
import {
  KNOWN_BULK_IMPORT_CODES,
  type BulkImportErrorCode,
} from '@workchat/shared-types';
import {
  bulkImportUsers,
  type BulkImportResult,
  type BulkImportRowError,
} from '../../api/admin-bulk-import';
// 채택 3단계 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import { bulkImportHelpHref } from '../HelpPage';
import { useAuthStore } from '../../store/authStore';
import {
  tSettings, getSettingsLang, subscribeSettingsLang,
  type SettingsI18nKey,
} from '../../lib/settings-i18n';

/**
 * ADMIN 전용 — User CSV 일괄 등록.
 *
 * 정책:
 *   - admin role 만 표시 (자기 가드)
 *   - 단일 file input + submit. 결과는 successCount + errors[] 표로 표시
 *   - CSV 컬럼 가이드 inline 안내 (email/name 필수, role/position/phone/password 선택)
 *   - F2: code 별 한국어 보조 안내 + severity 컬러 (severity-error/warning/info CSS class)
 *   - F3: errors > 5 일 때 상위 3 코드 빈도 요약
 *   - F4: DELETED_EMAIL_EXISTS 의 관리자 매뉴얼 link 자리 (placeholder)
 */

const ADMIN_ROLES = ['ADMIN', 'SYSTEM_ADMIN'] as const;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const FREQUENCY_SUMMARY_THRESHOLD = 5; // errors 가 이 값 초과 시 빈도 요약 표시
const FREQUENCY_TOP_N = 3;
// R1 봉합: 결과 테이블 soft-cap (chat-server MAX_ROWS=10_000 → errors 도 최대 10k 가능).
// 상위 N 건만 렌더 + 전체 보기 토글 — 진짜 가상화 (react-window) 대신 도메인 적합한 단순 cap.
// 빈도 요약 (`summary`) 이 이미 상위 패턴 노출 + admin 의 typical workflow 는 fix-and-reupload 라
// line-by-line 추적 의존도 낮음. cap 초과 시 토글로 전체 보기 가능 (DOM weight 명시 동의).
const MAX_VISIBLE_ERRORS = 200;

type Severity = 'error' | 'warning' | 'info';

interface CodeGuideEntry {
  severity: Severity;
  hintKey: SettingsI18nKey;
  actionKey?: SettingsI18nKey;
  actionHref?: string;
}

/**
 * 14 알려진 code × {severity, hintKey, actionKey?, actionHref?} 매핑 — Cycle 60 i18n 적용.
 *
 * cycle 60 변경: hint 가 hardcoded ko 문자열 → settings-i18n key 로 변경. ko/en/ja 모두 동등
 * 표현 제공. action 필드도 키 두 종 (manual / server_log).
 *
 * F4: DELETED_EMAIL_EXISTS / INSTITUTION_NOT_FOUND / FK_VIOLATION / PASSWORD_HASH_FAILED 의
 * action 필드로 admin 의 후속 조치 가이드. 매뉴얼 페이지 (`/help/admin/bulk-import`) 자체는
 * 별도 cycle (`bulk-import-admin-help-pages`) 신설 — 본 cycle 은 link 자리만.
 */
const CODE_GUIDE: Record<BulkImportErrorCode, CodeGuideEntry> = {
  EMAIL_MISSING:        { severity: 'error',   hintKey: 'settings.admin_import.code.EMAIL_MISSING' },
  NAME_MISSING:         { severity: 'error',   hintKey: 'settings.admin_import.code.NAME_MISSING' },
  EMAIL_INVALID:        { severity: 'error',   hintKey: 'settings.admin_import.code.EMAIL_INVALID' },
  EMAIL_TOO_LONG:       { severity: 'warning', hintKey: 'settings.admin_import.code.EMAIL_TOO_LONG' },
  NAME_TOO_LONG:        { severity: 'warning', hintKey: 'settings.admin_import.code.NAME_TOO_LONG' },
  EMPLOYEE_NUMBER_TOO_LONG: { severity: 'warning', hintKey: 'settings.admin_import.code.EMPLOYEE_NUMBER_TOO_LONG' },
  ROLE_INVALID:         { severity: 'warning', hintKey: 'settings.admin_import.code.ROLE_INVALID' },
  PASSWORD_TOO_SHORT:   { severity: 'warning', hintKey: 'settings.admin_import.code.PASSWORD_TOO_SHORT' },
  PASSWORD_HASH_FAILED: {
    severity: 'error',
    hintKey: 'settings.admin_import.code.PASSWORD_HASH_FAILED',
    actionKey: 'settings.admin_import.action.manual',
    actionHref: bulkImportHelpHref('passwordHashFailed'),
  },
  DUPLICATE_IN_CSV:     { severity: 'warning', hintKey: 'settings.admin_import.code.DUPLICATE_IN_CSV' },
  EMAIL_EXISTS:         { severity: 'info',    hintKey: 'settings.admin_import.code.EMAIL_EXISTS' },
  DELETED_EMAIL_EXISTS: {
    severity: 'warning',
    hintKey: 'settings.admin_import.code.DELETED_EMAIL_EXISTS',
    actionKey: 'settings.admin_import.action.manual',
    actionHref: bulkImportHelpHref('deletedEmail'),
  },
  INSTITUTION_NOT_FOUND: {
    severity: 'error',
    hintKey: 'settings.admin_import.code.INSTITUTION_NOT_FOUND',
    actionKey: 'settings.admin_import.action.manual',
    actionHref: bulkImportHelpHref('institutionNotFound'),
  },
  FK_VIOLATION: {
    severity: 'error',
    hintKey: 'settings.admin_import.code.FK_VIOLATION',
    actionKey: 'settings.admin_import.action.manual',
    actionHref: bulkImportHelpHref('fkViolation'),
  },
  CREATE_FAILED: {
    severity: 'error',
    hintKey: 'settings.admin_import.code.CREATE_FAILED',
    actionKey: 'settings.admin_import.action.server_log',
  },
};

const UNKNOWN_CODE_GUIDE: CodeGuideEntry = {
  severity: 'error',
  hintKey: 'settings.admin_import.code.UNKNOWN',
};

/**
 * T8/H1 봉합: KNOWN_BULK_IMPORT_CODES 를 `@workchat/shared-types` 에서 import (cross-package SSOT).
 *
 * 종전 (직전 cycle F7/H5) — 본 파일 자체 `Object.keys(CODE_GUIDE)` derive 였으나, 본 cycle
 * (bulk-import-frontend-types-ssot) 에서 shared-types 의 BULK_IMPORT_CODE_DOC 가 SSOT 이 됨 →
 * KNOWN 도 shared 에서 single source. CODE_GUIDE 의 14 entry 가 shared union 의 14 코드와 일치
 * (Record<BulkImportErrorCode, CodeGuideEntry> 의 exhaustive 강제) — drift 없음.
 *
 * (참고) UI 메타 (severity / hint / action / actionHref) 는 frontend 만의 책임 — shared 는 도메인
 * meaning + nextAction 만. 관심사 분리 유지.
 */

function guideFor(code: string): CodeGuideEntry {
  return KNOWN_BULK_IMPORT_CODES.has(code as BulkImportErrorCode)
    ? CODE_GUIDE[code as BulkImportErrorCode]
    : UNKNOWN_CODE_GUIDE;
}

interface CodeFrequency {
  code: string;
  count: number;
  severity: Severity;
}

/**
 * F3: client-side reduce — errors[] 의 code 별 빈도 누적 + 내림차순 sort + top-N 추출.
 *
 * server 의 audit metadata (countErrorCodes) 와 별개로 frontend 자체 집계 — 추가 API 호출 없이
 * 응답에 들어온 errors[] 만으로 산출. errors.length === 0 이면 빈 배열.
 */
export function summarizeCodes(
  errors: ReadonlyArray<BulkImportRowError>,
  topN: number = FREQUENCY_TOP_N,
): CodeFrequency[] {
  const map = new Map<string, number>();
  for (const e of errors) {
    map.set(e.code, (map.get(e.code) ?? 0) + 1);
  }
  // F7/M5 봉합: 동률 tie-break — count 내림차순 후 code 알파벳 오름차순.
  // server errors[] 순서가 향후 비결정적으로 변해도 화면 정렬은 결정적.
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, topN)
    .map(([code, count]) => ({ code, count, severity: guideFor(code).severity }));
}

export function AdminBulkImportUserSection() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role && (ADMIN_ROLES as ReadonlyArray<string>).includes(role);

  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // R1 봉합: errors > MAX_VISIBLE_ERRORS 시 상위 N 건 cap. admin 이 명시적으로 토글하면 전체 렌더.
  const [showAllErrors, setShowAllErrors] = useState(false);

  // P3/M6 봉합: dependency 를 narrow — 향후 setResult partial update 시 errors 동일하지만
  // reference 새 객체 → 1만 errors O(n log n) 재계산 회귀 방지.
  const summary = useMemo<CodeFrequency[]>(() => {
    if (!result) return [];
    if (result.errors.length <= FREQUENCY_SUMMARY_THRESHOLD) return [];
    return summarizeCodes(result.errors, FREQUENCY_TOP_N);
  }, [result?.errors]);

  // R1: errors.slice — 새 result 도착 시 cap 상태 reset (이전 토글 흔적 차단).
  const visibleErrors = useMemo(() => {
    if (!result) return [];
    if (showAllErrors || result.errors.length <= MAX_VISIBLE_ERRORS) return result.errors;
    return result.errors.slice(0, MAX_VISIBLE_ERRORS);
  }, [result?.errors, showAllErrors]);
  const hiddenErrorCount = result ? result.errors.length - visibleErrors.length : 0;

  if (!isAdmin) return null;

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    setResult(null);
    setShowAllErrors(false);
    if (f && f.size > MAX_FILE_SIZE_BYTES) {
      setError(tSettings('settings.admin_import.err_file_size', { mb: (f.size / 1024 / 1024).toFixed(2) }));
      setFile(null);
      return;
    }
    setFile(f);
  };

  const onSubmit = async () => {
    if (!file) {
      setError(tSettings('settings.admin_import.err_no_file'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    setShowAllErrors(false);
    try {
      const r = await bulkImportUsers(file);
      setResult(r);
    } catch (err) {
      const e = err as { response?: { data?: { code?: string; message?: string } }; message?: string };
      const code = e.response?.data?.code;
      const message = e.response?.data?.message ?? e.message ?? 'unknown';
      // err code 는 backend SSOT 라 그대로 표시 (영문 brackets + message)
      setError(code ? `[${code}] ${message}` : message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="settings-section">
      <h2>{tSettings('settings.admin_import.heading')}</h2>
      <p className="settings-section__hint">
        {tSettings('settings.admin_import.hint_columns_lead')} <code>email</code> {tSettings('settings.admin_import.hint_columns_required')} / <code>name</code> {tSettings('settings.admin_import.hint_columns_required')} /{' '}
        <code>role</code> {tSettings('settings.admin_import.hint_columns_optional')}, ADMIN | MANAGER | STAFF{tSettings('settings.admin_import.hint_columns_default')} /{' '}
        <code>position</code> / <code>phone</code> / <code>password</code> {tSettings('settings.admin_import.hint_password_optional')}
      </p>
      <p className="settings-section__hint">
        {tSettings('settings.admin_import.hint_limit')} <code>institutionId</code> {tSettings('settings.admin_import.hint_institution')}
      </p>
      <p className="settings-section__hint">
        {tSettings('settings.admin_import.hint_password_note')}
      </p>

      <div className="settings-form">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={onFileChange}
          disabled={submitting}
          aria-label={tSettings('settings.admin_import.aria_file')}
        />
        <Button
          variant="primary"
          size="compact"
          onClick={onSubmit}
          disabled={!file || submitting}
          loading={submitting}
        >
          {submitting ? tSettings('settings.admin_import.btn_uploading') : tSettings('settings.admin_import.btn_upload')}
        </Button>
      </div>

      {error && <div role="alert" className="settings-error">{error}</div>}

      {result && (
        <div className="settings-result">
          <p>
            <strong>{tSettings('settings.admin_import.summary_lead', { total: result.totalRows })}</strong>
            {' '}{tSettings('settings.admin_import.summary_success_fail', { success: result.successCount, failure: result.failureCount })}
            {result.aborted && <span className="settings-warn">{tSettings('settings.admin_import.aborted')}</span>}
          </p>
          {!result.auditPersisted && (
            <p role="alert" className="settings-warn">
              {tSettings('settings.admin_import.audit_failed')}
            </p>
          )}

          {summary.length > 0 && (
            <div className="settings-summary" aria-label={tSettings('settings.admin_import.summary_aria')}>
              <strong>{tSettings('settings.admin_import.summary_title', { n: summary.length })}</strong>
              <ul className="settings-summary__list">
                {summary.map((s) => (
                  <li key={s.code} className={`severity-${s.severity}`}>
                    <code>{s.code}</code> ({s.count}{tSettings('settings.admin_import.summary_count_suffix')})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.errors.length > 0 && (
            <>
              <table className="settings-table">
                <thead>
                  <tr>
                    <th>{tSettings('settings.admin_import.table.col_row')}</th>
                    <th>{tSettings('settings.admin_import.table.col_email')}</th>
                    <th>{tSettings('settings.admin_import.table.col_code')}</th>
                    <th>{tSettings('settings.admin_import.table.col_message')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleErrors.map((e, i) => {
                    const guide = guideFor(e.code);
                    return (
                      <tr key={`${e.row}-${i}`} className={`severity-${guide.severity}`}>
                        <td>{e.row}</td>
                        <td>{e.email ?? '-'}</td>
                        <td><code>{e.code}</code></td>
                        <td>
                          <div>{e.message}</div>
                          <div className="settings-cell-hint">{tSettings(guide.hintKey)}</div>
                          {guide.actionKey && (
                            <div className="settings-cell-action">
                              {guide.actionHref ? (
                                <a href={guide.actionHref} target="_blank" rel="noopener noreferrer">
                                  {tSettings(guide.actionKey)} ↗
                                </a>
                              ) : (
                                <span>{tSettings(guide.actionKey)}</span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {result.errors.length > MAX_VISIBLE_ERRORS && (
                <div className="settings-table-overflow" role="status">
                  {showAllErrors ? (
                    <>
                      <span>{tSettings('settings.admin_import.overflow_all_warn', { n: result.errors.length })}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="settings-table-toggle"
                        onClick={() => setShowAllErrors(false)}
                      >
                        {tSettings('settings.admin_import.overflow_show_top', { n: MAX_VISIBLE_ERRORS })}
                      </Button>
                    </>
                  ) : (
                    <>
                      <span>
                        {tSettings('settings.admin_import.overflow_top_with_hidden', { visible: MAX_VISIBLE_ERRORS, hidden: hiddenErrorCount })}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="settings-table-toggle"
                        onClick={() => setShowAllErrors(true)}
                      >
                        {tSettings('settings.admin_import.overflow_show_all', { n: result.errors.length })}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
